import { createContext, useContext, useState, useCallback, useMemo } from "react";
import type { Dispatch, ReactNode, SetStateAction } from "react";
import Cookies from "universal-cookie";
import { useQuery, useQueryClient, type Updater } from "@tanstack/react-query";
import { userApi, authApi } from "../services/endpoints";
import { MSG } from "../services/messages";
import { SESSION_CONFIG } from "../constants";
import type { AppUser } from "../types/entities";

/**
 * AuthContext - Manages authentication state and user data
 */

/** The shape of the `value` object provided by {@link AuthProvider}. */
export interface AuthContextValue {
  isAuth: boolean | null;
  setIsAuth: Dispatch<SetStateAction<boolean | null>>;
  userDetails: AppUser | null;
  // Cache writer with the same value-or-updater contract as setQueryData.
  setUserDetails: (
    value: Updater<AppUser | null | undefined, AppUser | null | undefined>
  ) => AppUser | null | undefined;
  token: string | null | undefined;
  setToken: Dispatch<SetStateAction<string | null | undefined>>;
  getUserDetails: () => Promise<void>;
  login: (
    email: string,
    password: string,
    rememberMe?: boolean
  ) => Promise<{ success: boolean; error?: string }>;
  logOut: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// The current user ("me") now lives in the TanStack Query cache instead of a
// local useState. /api/users/me returns a FLAT user object (no { data }
// envelope), so the queryFn unwraps the axios response's `.data`. The key and
// fetcher are static, so they live at module scope and are shared by both the
// live useQuery subscription and getUserDetails' imperative refetch.
export const ME_QUERY_KEY = ["me"];

const meQueryOptions = {
  queryKey: ME_QUERY_KEY,
  queryFn: async (): Promise<AppUser> => (await userApi.getById("me", { populate: "*" })).data,
  // user identity changes rarely; keep it cached for a while.
  staleTime: 5 * 60_000,
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  // Memoize cookies to prevent recreation on every render
  const cookies = useMemo(() => new Cookies(), []);
  const queryClient = useQueryClient();

  const [isAuth, setIsAuth] = useState<boolean | null>(null);
  const [token, setToken] = useState<string | null | undefined>(
    cookies.get<string | undefined>(SESSION_CONFIG.TOKEN_COOKIE_NAME)
  );

  // The "me" fetch, migrated from an imperative useState + call to a query. It
  // enables itself as soon as there's a token (mount with an existing cookie, or
  // right after login sets the token). `userDetails` mirrors the old state's
  // shape: the flat user object, defaulting to null so all the userDetails?.role
  // / userDetails?.email reads across the app keep working.
  const meQuery = useQuery({ ...meQueryOptions, enabled: !!token });
  const userDetails = meQuery.data ?? null;

  // Cache writer kept on the context for API parity with the old setUserDetails
  // (accepts a value or an updater fn, same as setQueryData).
  const setUserDetails = useCallback(
    (value: Updater<AppUser | null | undefined, AppUser | null | undefined>) =>
      queryClient.setQueryData<AppUser | null>(ME_QUERY_KEY, value),
    [queryClient]
  );

  // Thin wrapper preserving the getUserDetails() call site (useAppData's
  // load-on-auth effect). It forces a fresh /me fetch (staleTime: 0) and
  // reconciles isAuth exactly as the old imperative version did: no token →
  // false without hitting the API; success → true; error → false. Stable
  // identity (token + the stable queryClient) so the effect doesn't loop.
  const getUserDetails = useCallback(async () => {
    if (!token) {
      setIsAuth(false);
      return;
    }
    try {
      await queryClient.fetchQuery({ ...meQueryOptions, staleTime: 0 });
      setIsAuth(true);
    } catch (err) {
      setIsAuth(false);
      console.error("auth.getUserDetails failed", err);
    }
  }, [token, queryClient]);

  // Login function
  const login = useCallback(
    async (email: string, password: string, rememberMe = false) => {
      // Login failures (wrong credentials, etc.) are an expected outcome — let the
      // rejection propagate so the caller can show the user a message. They are NOT
      // logged/reported (the api interceptor also skips 4xx).
      const result = await authApi.login(email, password);
      // authApi.login returns an untyped axios body; describe the fields we read.
      const body: { jwt?: string; user?: AppUser | null } = result.data;

      if (body?.jwt) {
        // Set cookie first (synchronous). "Angemeldet bleiben" makes it a
        // PERSISTENT cookie (survives a browser restart) via maxAge; without it
        // the cookie stays a session cookie that the browser drops on close.
        cookies.set(SESSION_CONFIG.TOKEN_COOKIE_NAME, body.jwt, {
          path: "/",
          ...(rememberMe ? { maxAge: SESSION_CONFIG.REMEMBER_ME_MAX_AGE } : {}),
        });

        // Update state. Seed the ['me'] cache with the login response's user so
        // userDetails is populated synchronously; setting the token then enables
        // the query, and useAppData's getUserDetails() forces the full populated
        // /me fetch to replace this seed.
        setToken(body.jwt);
        setIsAuth(true);
        queryClient.setQueryData<AppUser | null>(ME_QUERY_KEY, body.user || null); // Assuming user data comes back with login

        return { success: true };
      }
      return { success: false, error: MSG.common.error };
    },
    [cookies, queryClient]
  );

  // Logout function
  const logOut = useCallback(() => {
    cookies.remove(SESSION_CONFIG.TOKEN_COOKIE_NAME, { path: "/" });
    setToken(null);
    setIsAuth(false);
    // Clearing the token disables the query; drop the cached user so nothing
    // reads a stale identity after logout.
    queryClient.removeQueries({ queryKey: ME_QUERY_KEY });
  }, [cookies, queryClient]);

  const value: AuthContextValue = {
    isAuth,
    setIsAuth,
    userDetails,
    setUserDetails,
    token,
    setToken,
    getUserDetails,
    login,
    logOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Custom hook to access auth state
 * @throws {Error} If used outside AuthProvider
 */
export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};
